import { useContext, useRef } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { resolveLink } from '../utils'

/**
 * ReactiveJSON primitive — renders an <a> tag with href/target/rel
 * resolved from either a plain URL string or a {url, target} object.
 *
 * YAML usage:
 *   - type: ResolvedLink
 *     value: ~~.ctaUrl
 *     attributes:
 *       class: "btn-primary"
 *       data-builder-field: ctaLabel
 *     preventNavigation: false   # optional, set true for placeholder links
 *     content: [...]             # children (text, icons, ...)
 */
export const ResolvedLink = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)

  const value = evaluateTemplateValue({
    valueToEvaluate: props.value,
    globalDataContext,
    templateContext,
  })
  const preventNavigation = evaluateTemplateValue({
    valueToEvaluate: props.preventNavigation,
    globalDataContext,
    templateContext,
  })

  const { href, target, rel } = resolveLink(value)
  const evaluatedAttrs = useEvaluatedAttributes(props.attributes)

  const onClick = preventNavigation ? (e) => e.preventDefault() : undefined

  // Le nœud sur lequel les actions d'ATTRIBUT écrivent. `SetAttributeValue` et
  // ses semblables sortent sans rien faire, et sans rien dire, quand
  // `attributesHolderRef` est vide : un `aria-current`, un `aria-expanded` ou un
  // `aria-pressed` déclaré sur un lien n'était jamais posé. Toutes les autres
  // primitives à actions du projet câblent déjà ce ref (StableTextField,
  // FacetList, Pagination, Modal…) ; ce lien était le seul à l'oublier.
  const linkRef = useRef(null)

  return (
    <ActionDependant {...props} attributesHolderRef={linkRef}>
      <a ref={linkRef} href={href} target={target} rel={rel} onClick={onClick} {...evaluatedAttrs}>
        {props.content && (
          <View
            currentData={currentData?.content}
            datafield="content"
            path={path + '.content'}
            props={props.content}
          />
        )}
      </a>
    </ActionDependant>
  )
}
